import { useRef, useState } from "react";
import type { ReactNode } from "react";

import { useTrackEvent } from "~/hooks/useTrackEvent";
import { getTextFromNode } from "~/utils/getTextFromNode";
import { VideoTrackingConfig, DEFAULT_VIDEO_TRACKING_CONFIG } from "./data";

type VideoTestimonialInput = {
    mp4: string;
    author: string;
    text: ReactNode;
};

/**
 * Shared play/pause + analytics behaviour for the EnhancedTestimonials video
 * slides (VideoTopSlide / VideoBottomSlide). Owns the video ref and started
 * state, and fires the play/stop tracking events; each slide keeps its own JSX.
 */
export const useVideoTestimonial = (
    { mp4, author, text }: VideoTestimonialInput,
    trackingConfig?: VideoTrackingConfig,
) => {
    const {
        playEventName = DEFAULT_VIDEO_TRACKING_CONFIG.playEventName,
        stopEventName = DEFAULT_VIDEO_TRACKING_CONFIG.stopEventName,
        widget = DEFAULT_VIDEO_TRACKING_CONFIG.widget,
        includeVslSound = DEFAULT_VIDEO_TRACKING_CONFIG.includeVslSound,
    } = trackingConfig ?? {};

    const sendEvent = useTrackEvent();
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [videoStarted, setVideoStarted] = useState(false);

    // Analytics expects a string; flatten JSX captions down to their text.
    const videoName = getTextFromNode(text) || undefined;

    const onClick = () => {
        if (!videoRef.current) return;

        if (videoStarted) {
            return videoRef.current.pause();
        } else {
            return videoRef.current.play();
        }
    };

    const onVideoPlay = () => {
        setVideoStarted(true);

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: playEventName,
                widget,
                meta_data: {
                    video_id: mp4,
                    video_name: videoName,
                    video_person_name: author,
                    click_text: "play video",
                    ...(includeVslSound && {
                        vsl_sound: !videoRef.current?.muted,
                    }),
                },
            },
        });
    };

    const onVideoPause = () => {
        setVideoStarted(false);

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: stopEventName,
                widget,
                meta_data: {
                    video_id: mp4,
                    video_name: videoName,
                    video_person_name: author,
                    click_text: "pause video",
                    ...(includeVslSound && {
                        vsl_sound: !videoRef.current?.muted,
                    }),
                },
            },
        });
    };

    return { videoRef, videoStarted, onClick, onVideoPlay, onVideoPause };
};

export default useVideoTestimonial;
